import { useEffect } from "react";

import { ApiError, apiFetch } from "@pontimed/api-client";
import { queryOptions, useQuery } from "@tanstack/react-query";
import { useAtom } from "jotai";

import { sessionStatusAtom, sessionUserAtom } from "../atoms";
import { refreshSession } from "../refresh";
import type { SessionStatus, SessionUser } from "../types";

const SESSION_QUERY_KEY = ["auth", "session"] as const;

/**
 * Shared session query definition. Use this for TanStack Router `loader`
 * prefetch:
 *   `loader: ({ context }) => context.queryClient.ensureQueryData(sessionQueryOptions)`
 * so the network request fires in parallel with the route chunk download,
 * and component mount sees a hot cache instead of triggering its own fetch.
 *
 * Both the route loader and `useSession()` reference the same `queryKey` →
 * single cache slot, no double-fetch, no drift between preload and component.
 */
export const sessionQueryOptions = queryOptions({
  queryKey: SESSION_QUERY_KEY,
  queryFn: async (): Promise<SessionUser | null> => {
    // Server contract:
    //   - 200 with SessionUser → authenticated
    //   - 200 with JSON `null` → anonymous (no access cookie present —
    //                             not an error, just logged out)
    //   - 401                  → token present but invalid/expired; try
    //                             /refresh and retry once.
    const fetchMe = () =>
      apiFetch.get<SessionUser | null>("/api/auth/me", { skipAuthRefresh: true });
    try {
      return await fetchMe();
    } catch (error) {
      if (!(error instanceof ApiError) || error.status !== 401) throw error;
      const refreshed = await refreshSession();
      if (!refreshed) return null;
      try {
        return await fetchMe();
      } catch (retryError) {
        if (retryError instanceof ApiError && retryError.status === 401) return null;
        throw retryError;
      }
    }
  },
  staleTime: 5 * 60_000,
  gcTime: 30 * 60_000,
  refetchOnWindowFocus: true,
  // 401 is terminal — the queryFn already runs refresh-and-retry internally,
  // so reaching the React Query error layer with a 401 means the user is
  // genuinely logged out. Don't loop.
  //
  // Everything else (5xx, network failure, CORS during apps/api restart) is
  // transient: dev `tsx watch` restarts take 3-8s, Fly machine cycles 1-3s.
  // Retry with backoff so the SPA self-heals without surfacing an error UI.
  retry: (failureCount, error) => {
    if (error instanceof ApiError && error.status === 401) return false;
    return failureCount < 3;
  },
  retryDelay: (attempt) => Math.min(1000 * 2 ** attempt, 8000),
});

export interface UseSessionResult {
  user: SessionUser | null;
  status: SessionStatus;
  refetch: () => Promise<unknown>;
}

/**
 * GET /api/auth/me — returns the JWT claims (userId, role, scope, ws?, jti).
 * Rich profile data (email, name, photoUrl) is NOT here — use useUserProfile().
 *
 * On 401 (access cookie expired) we attempt the refresh cookie once before
 * declaring "unauthenticated". Without this, the user appears logged out
 * every 15 min even though their refresh token is valid for 7+ days.
 *
 * The query definition lives in `sessionQueryOptions` so route loaders can
 * prefetch in parallel with chunk download via `queryClient.ensureQueryData`.
 */
export function useSession(): UseSessionResult {
  const [user, setUser] = useAtom(sessionUserAtom);
  const [status, setStatus] = useAtom(sessionStatusAtom);

  const query = useQuery(sessionQueryOptions);

  useEffect(() => {
    if (query.isLoading) return;
    if (query.isError) {
      // Distinguish DEFINITIVE auth failure (401 after refresh attempt → real
      // 401 ApiError) from a TRANSIENT API outage (5xx, network blip, CORS
      // because apps/api is restarting in dev). On a real 401 the queryFn
      // returns `null` — we never end up here. Anything that lands in the
      // error branch is non-auth: keep the previous status so the SPA does
      // not boot a still-logged-in user to /login while apps/api restarts.
      // The next staleTime tick (or refetchOnWindowFocus) will resolve it.
      const err = query.error as { status?: number } | null;
      const isHardAuthFailure = err?.status === 401;
      if (isHardAuthFailure) {
        setUser(null);
        setStatus("unauthenticated");
      }
      // else: leave status untouched (stays "loading" on first load, or
      // "authenticated" on subsequent flake). React Query will retry.
      return;
    }
    setUser(query.data ?? null);
    setStatus(query.data ? "authenticated" : "unauthenticated");
  }, [query.data, query.error, query.isLoading, query.isError, setUser, setStatus]);

  return { user, status, refetch: query.refetch };
}

export const sessionQueryKey = SESSION_QUERY_KEY;
